// src/web/scene.js
//
// Three.js scene + renderer + camera setup, designed so that the camera matches
// the virtual camera implicit in MediaPipe's facial transformation matrix.
//
// MediaPipe's transformation matrix is computed assuming a perspective camera
// at the origin, looking down -Z, with a vertical field of view of ~63°. To
// have the goggles overlay aligned with the user's actual face on the video,
// our Three.js PerspectiveCamera must match this configuration.
//
// The camera FOV vs the webcam's true FOV is generally close enough that no
// per-device calibration is needed. If the goggles look offset on a particular
// device, the fix is to nudge the per-SKU placement in the catalog, not to
// recalibrate the camera.

import {
  Scene, PerspectiveCamera, WebGLRenderer, AmbientLight, DirectionalLight,
  ACESFilmicToneMapping, SRGBColorSpace
} from 'three';

const _qs = new URLSearchParams(window.location.search);
const _fov = parseFloat(_qs.get('fov'));     // explicit vertical FOV override
const _dfov = parseFloat(_qs.get('dfov'));   // diagonal FOV of the real camera

// MediaPipe's transformation matrix is solved against a virtual camera with a
// vertical FOV of 63°. That is NOT the FOV of a real webcam: a laptop/USB
// webcam in landscape is ~40-45° vertical (≈72-78° diagonal), a phone front
// camera in portrait ≈ 60-65°. Rendering with 63° on a 16:9 desktop feed places
// the head ~1.6× too close to the virtual camera, which EXAGGERATES perspective
// across the width of the frame — the far-side lens shrinks dramatically at
// yaw and the temples splay. See calibrateCamera() / depthScaleFor().
export const MEDIAPIPE_VFOV_DEG = 63;

// Assumed diagonal FOV of the physical camera (deg). 74° is a good median for
// laptop webcams and phone selfie cameras; ?dfov= overrides, ?fov= forces the
// vertical FOV directly.
export const DEFAULT_DIAGONAL_FOV_DEG = Number.isFinite(_dfov) ? _dfov : 74;

const deg2rad = d => d * Math.PI / 180;
const rad2deg = r => r * 180 / Math.PI;

/**
 * Vertical FOV (deg) to render with for a capture of the given size, derived
 * from the assumed diagonal FOV. Landscape 16:9 @74° diag → ~40.6° vertical;
 * portrait 3:4 @74° → ~62° (≈ MediaPipe's own assumption, so phones are
 * essentially unchanged).
 */
export function estimateVerticalFov(videoW, videoH) {
  if (Number.isFinite(_fov)) return _fov;
  if (!videoW || !videoH) return MEDIAPIPE_VFOV_DEG;
  const tanD = Math.tan(deg2rad(DEFAULT_DIAGONAL_FOV_DEG) / 2);
  const tanV = tanD * videoH / Math.hypot(videoW, videoH);
  return rad2deg(2 * Math.atan(tanV));
}

/**
 * Factor to multiply MediaPipe's translation Z by so that a face solved under
 * the 63° virtual camera projects to the SAME screen position and size under
 * the real-FOV camera, but with correct (weaker) perspective:
 *   z' = z · tan(63°/2) / tan(vfov/2)
 * X and Y translation stay unchanged (see derivation in docs/ANALYSIS.md).
 */
export function depthScaleFor(vfovDeg) {
  return Math.tan(deg2rad(MEDIAPIPE_VFOV_DEG) / 2) / Math.tan(deg2rad(vfovDeg) / 2);
}

export function createScene({ canvas }) {
  const scene = new Scene();
  scene.background = null;   // transparent — the <video> element shows through

  const camera = new PerspectiveCamera(MEDIAPIPE_VFOV_DEG, 1, 1, 1000);
  camera.position.set(0, 0, 0);
  camera.lookAt(0, 0, -1);

  const renderer = new WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    premultipliedAlpha: true,
    powerPreference: 'high-performance'
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  // Lighting — the environment map carries most of the lighting via PBR.
  // Add a small ambient and a key light to lift the frame edges.
  const ambient = new AmbientLight(0xffffff, 0.35);
  scene.add(ambient);

  const key = new DirectionalLight(0xffffff, 0.55);
  key.position.set(2, 3, 4);
  scene.add(key);

  // Track of objects we may want to clean up later.
  return { scene, camera, renderer, lights: { ambient, key } };
}

/**
 * Size the renderer and camera to match the VIDEO's intrinsic aspect ratio,
 * NOT the (possibly differently-shaped) stage box.
 *
 * Why: MediaPipe computes its transformation matrix against the full camera
 * frame (e.g. 1280×720, 16:9). If we render with the stage's aspect (e.g. 3:4)
 * the glasses would project to the wrong horizontal position relative to the
 * cover-cropped video. So we render at the video's aspect and let CSS
 * object-fit:cover crop the canvas identically to the video. The camera aspect
 * must equal videoWidth/videoHeight.
 *
 * @param {WebGLRenderer} renderer
 * @param {PerspectiveCamera} camera
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLVideoElement} [video]  to read the true capture aspect
 */
export function fitToCanvas(renderer, camera, canvas, video) {
  // Determine the aspect we must render at = the video's intrinsic aspect.
  let aspect, videoH = 0;
  if (video && video.videoWidth && video.videoHeight) {
    aspect = video.videoWidth / video.videoHeight;
    videoH = video.videoHeight;
  } else {
    aspect = 16 / 9; // sensible default until the video reports its size
  }

  // Backing resolution. Since frame-locking renders the camera feed THROUGH this
  // canvas, the backing store must match the video's native resolution or the
  // image is downsampled and looks soft (a 720p target blurs a 1080p Brio feed).
  // Render at the video's native height, capped at 1440 for performance, so the
  // background stays crisp. CSS cover-crops it into the stage.
  // ?res= caps the backing height (e.g. ?res=720) to trade sharpness for fps.
  const resCap = parseFloat(_qs.get('res'));
  const cap = Number.isFinite(resCap) ? resCap : 1440;
  const baseHeight = Math.min(Math.max(videoH || 720, Math.min(720, cap)), cap);
  const w = Math.round(baseHeight * aspect);
  const h = baseHeight;
  // Pixel ratio 1: the backing store already matches the video's native
  // resolution, which is the sharpness ceiling. Multiplying by devicePixelRatio
  // would render a 1080p source at 4K — no quality gain, big GPU cost.
  renderer.setPixelRatio(1);
  renderer.setSize(w, h, false);   // false = don't touch the CSS size
  camera.aspect = aspect;
  // Calibrated FOV (see estimateVerticalFov). Recomputed whenever the video
  // reports its real dimensions.
  if (video && video.videoWidth && video.videoHeight) {
    const f = estimateVerticalFov(video.videoWidth, video.videoHeight);
    if (Math.abs(f - camera.fov) > 1e-3) {
      camera.fov = f;
      console.log(`[scene] camera vFOV ${f.toFixed(1)}° for ${video.videoWidth}×${video.videoHeight} (depth scale ${depthScaleFor(f).toFixed(3)})`);
    }
  }
  camera.updateProjectionMatrix();
}
